"use client"
import React, { useState, useEffect, useRef, Fragment } from "react";
import useApi from "@/components/api/useApi";
import { useConfig } from "@/lib/providers/ConfigProvider";
import { Container, Row, Col, Collapse, Modal, Ratio } from "react-bootstrap";
import MapBoxMapWrapper from "@/components/shared/MapBoxMapWrapper";
import MapPointView from "@/components/maps/MapPointView";
import MapLayerView from "@/components/maps/MapLayerView";
import PageScrollProgressBar from "@/components/shared/PageScrollProgressBar";

import { useMediaQuery } from "react-responsive";

import "@/components/maps/MapView.css";
import "@/components/maps/MapAndPins.css";
import useUtils from "@/components/shared/useUtils";
import TagsFilter from "@/components/shared/TagsFilter";
import PinLayerPointPopup from "@/components/assetmanagement/PinLayerPointPopup";
import ParentContentThumbnail from "@/components/shared/ParentContentThumbnail";
import UralityBrandedModal from "@/components/shared/UralityBrandedModal";
import UralityImage from "@/components/shared/UralityImage";
import MobileDrawer from "@/components/shared/MobileDrawerWrapper";

const MapAndPins = ({
  name,
  mapView,
  points,
  showProgress,
  external,
  hasHeader,
  supportsMediaSubmissions,
  fullscreenEnabled = true,
  fullscreenMode = false,
  onFullscreenToggle,
  enableComments = false,
  welcomeMessage = false,
  appendIntroduction
}) => {
  const { webUrl } = useConfig();
  const url = `${webUrl}${name}/stories/${mapView.id}`;

  const defaultPinLayer = {
    label: "Pins",
    value: "DEFAULT",
    layerMarkerType: "DEFAULT",
    layerMarkerRef: "006E78"
  };

  const [locationList, setLocationList] = useState(null);
  const [filteredLocationList, setFilteredLocationList] = useState(null);
  const [mapLayers, setMapLayers] = useState(null);
  const [selectedLayers, setSelectedLayers] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [previewMarker, setPreviewMarker] = useState(null);
  const [showLayerView, setShowLayerView] = useState(false);
  const [mapPoints, setMapPoints] = useState(null);
  const [filteredPoints, setFilteredPoints] = useState(null);
  const [progressTick, setProgressTick] = useState(0);
  const [showMapInfo, setShowMapInfo] = useState(false);
  const [thumbnailContent, setThumbnailContent] = useState(null);

  const [publicPinLayers, setPublicPinLayers] = useState([]);
  const [selectedPinLayerPoint, setSelectedPinLayerPoint] = useState(null);
  const [mapPopup, setMapPopup] = useState(null);

  const [loaded, setLoaded] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [interactive, setInteractive] = useState(false);
  const [defaultCenter, setDefaultCenter] = useState(null);

  const api = useApi();
  const utils = useUtils();
  const isSm = useMediaQuery({ maxWidth: 767.98 });

  const observer = useRef();
  const handleScroll = useRef(true);

  const isPhotoMap = mapView?.storyMapType === "PHOTO";

  useEffect(() => {
    if (fullscreenMode || isPhotoMap) {
      setFullscreen(true);
    }
  }, [fullscreenMode, isPhotoMap]);

  useEffect(() => {
    if (welcomeMessage) {
      setShowMapInfo(welcomeMessage);
    }
  }, [welcomeMessage]);

  useEffect(() => {
    if (mapPoints && mapPoints.length > 0) {
      const handleIntersection = (entries, observer) => {
        var stop = false;
        if (handleScroll.current) {
          entries.map(entry => {
            if (entry.isIntersecting && !stop) {
              var point = mapPoints.find(pt => pt.id === entry.target.id);
              if (point) {
                stop = true;
                onMarkerFocused(point);
              }
            }
          });
        }
      };
      const windowHeight = window.innerHeight;
      const observerOptions = isSm
        ? {
            root: null,
            rootMargin: `0px 0px -${windowHeight - 32}px 0px`,
            // rootMargin: hasHeader ? `0px 0px -${windowHeight - 318}px 0px` : `-240px 0px -${windowHeight-230}px 0px`,
            threshold: 0.01
          }
        : {
            // root: document.querySelector("#map-and-pins"),
            root: null,
            rootMargin: `0px 0px -${windowHeight - 32}px 0px`,
            // rootMargin: hasHeader ? `-108px 0px -${windowHeight-108}px 0px` : `-40px 0px -${windowHeight-40}px 0px`,
            threshold: 0.01
          };

      if (!observer.current) {
        // console.log( "setting up IntersectionObserver");
        observer.current = new IntersectionObserver(
          handleIntersection,
          observerOptions
        );

        const entries = document.querySelectorAll("div.scrollable-view-card");
        // console.log(entries);
        if (entries) {
          entries.forEach((entry, index) => {
            // console.log("observe", entry);
            observer.current.observe(entry);
          });
        }
      }
    }
  }, [loaded]);

  useEffect(() => {
    if (mapView) {
      if (!thumbnailContent) {
        setThumbnailContent(
          <ParentContentThumbnail
            imageId={mapView.coverPhotoId}
            title={mapView.title}
            description={mapView.text}
            url={url}
            // additionalText={pinLayerState.additionalText}
          />
        );
      }
      if (points) {
        points.forEach((point, index) => {
          point["index"] = index + 1;
        });
        var addDefaultLayer = points.findIndex(pt => !pt.layer) > -1;
        var visibleLayers = [];
        if (mapView.layers && mapView.layers.length > 0) {
          var newMapLayers = addDefaultLayer ? [defaultPinLayer] : [];
          newMapLayers = newMapLayers.concat(
            mapView.layers.filter(l => l.visible)
          );
          setMapLayers(newMapLayers);
          visibleLayers = newMapLayers.map(l => l.value);
          setSelectedLayers(visibleLayers);
          // setMapLayers(mapView.layers);
        }

        var theLocationList = points.map((point, index) => {
          if (
            point.location &&
            ((point.layer && visibleLayers.includes(point.layer)) ||
              !point.layer)
          ) {
            return {
              index: point.index,
              latitude: point.location.latitude,
              longitude: point.location.longitude,
              id: point.id,
              markerImageId: null,
              layer: point.layer ?? "DEFAULT",
              label: point.title,
              pinText: point.pinText ?? "",
              firstImageId: point.assets?.find(a => a.assetType === "IMAGE")?.id ?? null
            };
          } else {
            return null;
          }
        });
        theLocationList = theLocationList.filter(loc => loc != null);
        setLocationList(theLocationList);
        setMapPoints(points);
        if (theLocationList.length > 0) {
          setDefaultCenter(utils.createFitBounds(theLocationList));
        } else {
          setDefaultCenter(mapView.location ?? mapView.owner.location);
        }

        if (
          mapView &&
          mapView.assetGroupReferenceIds &&
          mapView.assetGroupReferenceIds.length > 0
        ) {
          api
            .getPublicPinLayers(name, mapView.assetGroupReferenceIds)
            .then(pinLayers => {
              setPublicPinLayers(pinLayers);
            })
            .catch(error => {});
        }

        setTimeout(() => {
          setLoaded(true);
        }, 500);
      }
    }
  }, [mapView, points]);

  useEffect(() => {
    if (filteredPoints) {
      setFilteredLocationList(filteredPoints.map(fp => fp.id));
    } else {
      setFilteredLocationList(null);
    }
  }, [filteredPoints]);

  const clearPinSelection = () => {
    setSelectedMarker(null);
    setPreviewMarker(null);
  };

  const setActiveLocation = (location) => {
    if (isSm) {
      setPreviewMarker(location);
      setSelectedMarker(null);
    } else {
      setSelectedMarker(location);
      setPreviewMarker(null);
    }
  };

  const navigatePinPreview = direction => {
    const activeLocation = isSm ? previewMarker : selectedMarker;
    if (!activeLocation || !locationList?.length) {
      return;
    }

    const idx = locationList.findIndex(loc => loc.id === activeLocation.id);
    const nextIdx =
      direction === "prev"
        ? idx <= 0
          ? locationList.length - 1
          : idx - 1
        : idx >= locationList.length - 1
        ? 0
        : idx + 1;
    const nextLocation = locationList[nextIdx];

    if (isSm) {
      setPreviewMarker(nextLocation);
      if (selectedMarker) {
        setSelectedMarker(nextLocation);
      }
    } else {
      setSelectedMarker(nextLocation);
    }

    const pointIndex = mapPoints.findIndex(pt => pt.id === nextLocation.id);
    if (pointIndex > -1) {
      setProgressTick(pointIndex);
    }
  };

  const onLocationClicked = location => {
    // console.log("MapAndPins onLocationClicked", location);
    handleScroll.current = false;
    setActiveLocation(location);
    var pointIndex = mapPoints.findIndex(pt => pt.id === location.id);
    if (pointIndex > -1) {
      setProgressTick(pointIndex);
    }
    var el = document.getElementById("anchor-" + location.id);
    if (el != null) {
      el.scrollIntoView({ block: "center" });
      el.scrollIntoView({ behavior: "smooth" });
      // el.classList.add('selected');
    }
    setTimeout(() => {
      handleScroll.current = true;
    }, 2000);
  };

  const onMarkerFocused = point => {
    // console.log("MapAndPins onMarkerFocused", point, handleScroll);
    if (handleScroll) {
      if (locationList) {
        let location = locationList.find(loc => loc.id === point.id);
        if (location != null) {
          if (isSm) {
            setPreviewMarker(location);
          } else {
            setSelectedMarker(location);
          }
        }

        var pointIndex = mapPoints.findIndex(pt => pt.id === point.id);
        if (pointIndex > -1) {
          setProgressTick(pointIndex);
        }
      }
    }
  };

  const onCardClicked = (evt, point) => {
    // console.log("MapAndPins onCardClicked", point);
    // setHandleScroll(false);
    // handleScroll = false;
    handleScroll.current = false;
    evt.preventDefault();
    evt.stopPropagation();

    var el = document.getElementById("anchor-" + point.id);
    if (el != null) {
      el.scrollIntoView({ behavior: "smooth" });
      // el.classList.add('selected');
    }

    if (locationList) {
      let location = locationList.find(loc => loc.id === point.id);
      if (location != null) {
        if (isSm) {
          if (previewMarker?.id !== location.id) {
            setPreviewMarker(location);
            setSelectedMarker(null);
          }
        } else if (selectedMarker?.id !== location.id) {
          setSelectedMarker(location);
        }
      }

      var pointIndex = mapPoints.findIndex(pt => pt.id === point.id);
      if (pointIndex > -1) {
        setProgressTick(pointIndex);
      }
    }
    setTimeout(() => {
      // setHandleScroll(true);
      handleScroll.current = true;
      // handleScroll = true;
    }, 2000);
  };

  const onAssetGroupPointClicked = (evt, pinLayerId, point) => {
    clearPinSelection();
    var pinLayer = publicPinLayers.find(pl => pl.id === pinLayerId);
    if (pinLayer) {
      var selectedPinLayerPoint = pinLayer.points.find(p => p.id === point.id);
      setSelectedPinLayerPoint(selectedPinLayerPoint);
    }
  };

  useEffect(() => {
    if (selectedPinLayerPoint) {
      var popup = <PinLayerPointPopup pinLayerPoint={selectedPinLayerPoint} />;
      setMapPopup(popup);
    }
  }, [selectedPinLayerPoint]);

  // const renderExploreButton = () => {
  //   return (
  //     <button
  //       type={"button"}
  //       className={"btn-explore-map btn btn-secondary with-icon"}
  //       onClick={() => {
  //         setInteractive(!interactive);
  //         setSelectedMarker(null);
  //         setSelectedPinLayerPoint(null);
  //         setFullscreen(!fullscreen);
  //         if (onFullscreenToggle) {
  //           onFullscreenToggle(!fullscreen);
  //         }
  //       }}
  //     >
  //       <span className="material-icons">
  //         {interactive ? "auto_stories" : "map"}
  //       </span>
  //       <span>{interactive ? "Story Mode" : "Explore the Map"}</span>
  //     </button>
  //   );
  // };

  const showMap = () => {
    return (
      <>
        <div
          className={`the-map ${external ? "embedded" : ""} ${
            showProgress ? "progress-bar-offset" : ""
          } ${fullscreen ? "fullscreen" : ""}`}
        >
          <div className={"upper-right-map"}>

            {external && (
              <div className={`${showMapInfo ? "active" : ""}`}>
                <div
                  className={`info-icon ${showMapInfo ? "active" : ""}`}
                  onClick={() => setShowMapInfo(true)}
                >
                  <span className="material-icons">info</span>
                </div>
                <UralityBrandedModal
                  show={showMapInfo}
                  size="md"
                  fullscreen={isSm}
                  centered
                  onHide={() => setShowMapInfo(false)}
                >
                  <>{thumbnailContent}</>
                  <div className="center-child-div-parent">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => setShowMapInfo(false)}
                    >
                      Go to Map
                    </button>
                  </div>
                </UralityBrandedModal>
              </div>
            )}
          </div>

          <MapBoxMapWrapper
            addClass={isSm ? "mapbox-mobile-map" : ""}
            height={
              isSm ? (external ? "calc(100vh - 68px)" : "240px")
                : showProgress
                ? "calc(100vh - 42px)"
                : "calc(100vh - 68px)"
            }
            width="100%"
            numbered={mapView.supportsNumbering ? mapView.numbered : false}
            locations={locationList ?? []}
            filter={filteredLocationList}
            routes={[]}
            layers={mapLayers}
            selectedLayers={selectedLayers ?? mapLayers}
            selectedLocation={isSm ? previewMarker : selectedMarker}
            onLocationClicked={onLocationClicked}
            onFeatureClicked={null}
            onMapClicked={clearPinSelection}
            addMyLocation={true}
            defaultCenter={
              defaultCenter ?? mapView.location ?? mapView.owner.location
            }
            defaultZoom={18}
            fullscreenEnabled={false}
            // onFullscreenToggle={toggle => {
            //   setSelectedMarker(null);
            //   setFullscreen(toggle);
            //   if (onFullscreenToggle) {
            //     onFullscreenToggle(toggle);
            //   }
            // }}
            assetGroups={publicPinLayers}
            onAssetGroupPointClicked={onAssetGroupPointClicked}
            mapToggle={{
              pins: true,
              regions: true,
              routes: false,
              waypoints: true,
              assetGroups: true,
              features: true
            }}
            popupObject={mapPopup}
            horizontalOffset={isSm ? 0 : screen.width * 0.35}
            interactive={true}
            photoMapMode={isPhotoMap}
            photoMapConfig={mapView?.photoMapConfig ?? null}
            coverPhotoId={mapView?.coverPhotoId ?? null}
          />
        </div>
      </>
    );
  };

  const onPrevPoint = () => {
    navigatePinPreview("prev");
  };

  const onNextPoint = () => {
    navigatePinPreview("next");
  };

  const getMapPointForMarker = marker => {
    return mapPoints?.find(mp => mp.id === marker.id) ?? null;
  };

  const truncateText = (text, maxLength = 255) => {
    if (!text) {
      return "";
    }
    if (text.length <= maxLength) {
      return text;
    }
    return `${text.slice(0, maxLength).trim()}...`;
  };

  const pointContent = marker => {
    var mapPoint = mapPoints.find(mp => mp.id === marker.id);
    if (mapPoint) {
      return (
        <MapPointView
          name={name}
          // className={`${className} scrollable-view-card`}
          external={external}
          // wrapperClassName={
          //   index === progressTick ? "selected" : ""
          // }
          ownerId={mapView.owner.id}
          mapId={mapView.id}
          point={mapPoint}
          numbered={mapView.supportsNumbering ? mapView.numbered : false}
          // onClick={evt => onCardClicked(evt, point)}
          supportsMediaSubmissions={supportsMediaSubmissions}
          enableComments={enableComments}
        />
      );
    } else {
      return <></>;
    }
  };

  const determinePointClassName = point => {
    let className = "";
    // Check if layers are selected and match the point's layer
    if (selectedLayers) {
      if (point.layer) {
        className = selectedLayers.includes(point.layer) ? "" : "hide";
      } else {
        className = selectedLayers.includes("DEFAULT") ? "" : "hide";
      }
    }
    if (filteredPoints) {
      // If points are filtered, show only the ones that match
      className =
        filteredPoints.findIndex(pt => pt.id === point.id) > -1 ? "" : "hide";
    }

    return className;
  };

  const renderGroup = (group, index, subgroup = false) => {
    return (
      <>
        <Row key={`${group.id}-${index}`}>
          <Col>
            {subgroup ? <h3>{group.title}</h3> : <h2>{group.title}</h2>}
            {group.text && <p>{group.text}</p>}
          </Col>
        </Row>
        {group.pointIds &&
          group.pointIds.length > 0 &&
          group.pointIds.map((point, index) => {
            // console.log("renderGroup point", point);
            let pointToRender = mapPoints.find(pt => pt.id === point.id);
            if (pointToRender) {
              return <Fragment key={index}>{renderPoint(pointToRender, index, group)}</Fragment>;
            }
          })}
        {group.subGroups &&
          group.subGroups.length > 0 &&
          group.subGroups.map((subgroup, index) => {
            return <Fragment key={index}>{renderGroup(subgroup, index, true)}</Fragment>;
          })}
      </>
    );
  };

  const renderPoint = (point, index, group) => {
    // console.log("renderPoint point", point,index,group);
    let className = determinePointClassName(point);
    return (
      <Row key={`${group ? `${group.id}-point` : "point"}-${index}`}>
        <Col id="map-content-col">
          <MapPointView
            name={name}
            className={`${className} scrollable-view-card`}
            external={external}
            wrapperClassName={index === progressTick ? "selected" : ""}
            ownerId={mapView.owner.id}
            mapId={mapView.id}
            point={point}
            numbered={mapView.supportsNumbering ? mapView.numbered : false}
            onClick={evt => onCardClicked(evt, point)}
            supportsMediaSubmissions={supportsMediaSubmissions}
            enableComments={enableComments}
          />
        </Col>
      </Row>
    );
  };

  const renderTagsFilter = () => {
    return (
      // <Container
      //     className={`tags-filter-container layers-enabled ${
      //         fullscreen ? "fullscreen" : ""
      //     } ${showLayerView ? "hide" : ""}`}
      // >
      <Row>
        <Col>
          <TagsFilter
            scrollable={!isSm}
            points={mapPoints}
            onSelect={filtered => {
              clearPinSelection();
              setFilteredPoints(filtered);
            }}
          />
        </Col>
      </Row>
      // </Container>
    );
  };

  const renderSelectedPinPanel = () => {
    if (!selectedMarker) {
      return null;
    }

    return (
      <div className="selected-pin-wrapper">
        <div className="selected-pin-header">

        <button
            type="button"
            className="selected-pin-preview-nav clear"
            onClick={() => onPrevPoint()}
          >
            <span className="material-icons">keyboard_arrow_left</span>
          </button>
          <button
            type="button"
            className="selected-pin-preview-nav clear"
            onClick={() => onNextPoint()}
          >
            <span className="material-icons">keyboard_arrow_right</span>
          </button>
          <div className="flex-grow" />
          <div className="clickable" onClick={() => setSelectedMarker(null)}>
            <span className="material-icons">close</span>
          </div>
        </div>
        <div className="selected-pin-content">
          {pointContent(selectedMarker)}
        </div>
      </div>
    );
  };

  const renderPinPreviewBar = () => {
    if (!previewMarker) {
      return null;
    }

    const point = getMapPointForMarker(previewMarker);
    const imageId =
      previewMarker.firstImageId ??
      point?.assets?.find(asset => asset.assetType === "IMAGE")?.id;
    const title = point?.title ?? previewMarker.label;
    const description = truncateText(point?.text, 255);

    return (
      <div className="selected-pin-preview-shell is-visible">
        <div
          className="selected-pin-preview-card clickable"
          onClick={() => setSelectedMarker(previewMarker)}
        >
          {imageId && (
            <div className="selected-pin-preview-image">
              <UralityImage imageId={imageId} size="sm" />
            </div>
          )}
          <div className="selected-pin-preview-body">
            {title && <h3 className="selected-pin-preview-title">{title}</h3>}
            {description && (
              <p className="selected-pin-preview-description">{description}</p>
            )}
          </div>
        </div>
        <div className="selected-pin-preview-nav-row">
          <button
            type="button"
            className="selected-pin-preview-nav clear"
            onClick={() => navigatePinPreview("prev")}
          >
            <span className="material-icons">keyboard_arrow_left</span>
          </button>
          <button
            type="button"
            className="selected-pin-preview-nav clear"
            onClick={() => navigatePinPreview("next")}
          >
            <span className="material-icons">keyboard_arrow_right</span>
          </button>
        </div>
      </div>
    );
  };

  const renderMobilePinDetailPanel = () => {
    if (!selectedMarker) {
      return null;
    }

    return (
      <div className="selected-pin-wrapper">
        <div className="selected-pin-header">
          <div className="clickable" onClick={() => setSelectedMarker(null)}>
            <span className="material-icons">expand_more</span>
          </div>
          <div className="flex-grow" />
          <div className="clickable" onClick={() => onPrevPoint()}>
            <span className="material-icons">arrow_back_ios</span>
          </div>
          <div className="clickable" onClick={() => onNextPoint()}>
            <span className="material-icons">arrow_forward_ios</span>
          </div>
        </div>
        <div className="selected-pin-content">
          {pointContent(selectedMarker)}
        </div>
      </div>
    );
  };

  if (mapView && loaded) {
    return (
      <div
        id="map-and-pins"
        className={`map-and-pins${external ? " embedded" : ""}${
          fullscreen ? " fullscreen" : ""
        }${hasHeader ? " has-header" : ""}${
          isSm ? " sm-layout" : " lg-layout"
        }${showProgress ? " progress-bar-offset" : ""}`}
      >
        {showProgress && (
          <PageScrollProgressBar
            className={`map-and-pins-progress ${
              fullscreen ? "fullscreen" : ""
            }`}
            width="100vw"
            foreground="#1565C0"
          />
        )}

        <div
          id="scroll-anchor"
          className={`${fullscreen ? " fullscreen" : ""}${
            hasHeader ? " has-header" : ""
          }`}
        />
        <div className={`map-container ${isSm ? "map-top" : "map-bg"}`}>
          {showMap()}
        </div>

        <div
          className={`selected-pin ${fullscreen ? "fullscreen" : ""} ${
            selectedMarker ? "show-content" : ""
          }`}
        >
          <Collapse in={fullscreen && selectedMarker}>
            <div style={{ width: "100%" }}>{renderSelectedPinPanel()}</div>
          </Collapse>
        </div>

        {isSm && fullscreen && previewMarker && !selectedMarker && (
          renderPinPreviewBar()
        )}

        <Container
          id="map-pin-container"
          className={`pin-list${interactive ? " interactive" : ""}${
            fullscreen ? " fullscreen" : ""
          }`}
        >
          {appendIntroduction && (
            <div className="story-mode">
              <div className={"default-card-wrapper"}>
                {mapView.coverPhotoId && (
                  <Ratio aspectRatio={"16x9"}>
                    <UralityImage imageId={mapView.coverPhotoId} size={"md"} />
                  </Ratio>
                )}
                <br />
                <h2>{mapView.title}</h2>
                {mapView.text && <p>{mapView.text}</p>}
              </div>
            </div>
          )}

          {mapView.groups && mapView.groups.length > 0 ? (
            <>
              {renderTagsFilter()}
              {mapView.groups.map((group, index) => (
                <div className="story-mode">{renderGroup(group, index)}</div>
              ))}
            </>
          ) : (
            <>
              {renderTagsFilter()}
              {mapPoints.map((point, index) => {
                let className = determinePointClassName(point);
                return (
                  <Row key={index}>
                    <Col id="map-content-col">
                      <MapPointView
                        name={name}
                        className={`${className} scrollable-view-card`}
                        external={external}
                        wrapperClassName={
                          index === progressTick ? "selected" : ""
                        }
                        ownerId={mapView.owner.id}
                        mapId={mapView.id}
                        point={point}
                        numbered={
                          mapView.supportsNumbering ? mapView.numbered : false
                        }
                        onClick={evt => onCardClicked(evt, point)}
                        supportsMediaSubmissions={supportsMediaSubmissions}
                        enableComments={enableComments}
                      />
                    </Col>
                  </Row>
                );
              })}
            </>
          )}
        </Container>
      </div>
    );
  } else {
    return <></>;
  }
};
export default MapAndPins;
